import { Component, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import {
  VNG_DIALOG_DATA,
  VngCarouselComponent,
  VngCarouselSlide,
  VngCarouselSlideClickEvent,
  VngDialogRef,
} from "@vengreso/angular-components";
import { Router } from "@angular/router";
import { AuthService } from "src/app/core/core-services/services/auth.service";
import { UserDetails } from "src/app/core/models/user.model";
import { DialogService } from "src/app/core/core-services/services/dialog.service";
import { ExtensionService } from "src/app/core/core-services/services/extension.service";
import { SharedDataService } from "src/app/core/core-services/services/shared-data.service";
import { GlobalHookService } from "src/app/global-hook.service";
import { VideoComponent } from "../../video/video.component";
import { Observable, lastValueFrom } from "rxjs";
import { YouTubePlayer } from "@angular/youtube-player";
import { DataService } from "src/app/core/core-services/services/dataservices/data.service";
import { StripeService } from "src/app/core/core-services/services/stripe.service";
import { WELCOME_NAG_DIALOG_CLASS } from "../../welcome/welcome.component";
import {
  OnboardingProgressService,
  OnboardingStepKey,
  WELCOME_MODAL_STEP_ORDER,
} from "src/app/core/core-services/services/onboarding-progress.service";

/** The opaque payload each walk-me carousel slide carries — handed straight to VideoComponent on click. */
interface WalkMeVideoSlideData {
  video: string;
  title: string;
}

/**
 * Maps a raw YouTube embed URL + duration to a vng-carousel slide. vng-carousel takes
 * plain image URLs (no YouTube-specific logic), so the embed URL is converted to its
 * thumbnail here; the duration becomes the bottom-right label badge, and the original
 * {video, title} pair rides along in `data` so (slideClick) can hand it to VideoComponent
 * unchanged.
 */
function toWalkMeSlide(video: string, title: string): VngCarouselSlide {
  const videoId = video.split("/").pop();
  return {
    image: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
    label: title,
    alt: title,
    data: { video, title } as WalkMeVideoSlideData,
  };
}

@Component({
    selector: "app-welcome-modal",
    templateUrl: "./welcome-modal.component.html",
    styleUrls: ["./welcome-modal.component.scss"],
    standalone: false
})
export class WelcomeModalComponent implements OnInit, OnDestroy {
  carouselSlides1: VngCarouselSlide[] = [
    toWalkMeSlide("https://www.youtube.com/embed/AIPAVkFVQS0", "4:05"),
    toWalkMeSlide("https://www.youtube.com/embed/w6QeLZoyzTA", "4:36"),
    toWalkMeSlide("https://www.youtube.com/embed/JmHc-eY1ga0", "1:54"),
    toWalkMeSlide("https://www.youtube.com/embed/HxQS-WX9wTI", "2:16"),
    toWalkMeSlide("https://www.youtube.com/embed/Dm9CiRg5qJA", "2:40"),
    toWalkMeSlide("https://www.youtube.com/embed/2oomEp7x57U", "2:27"),
    toWalkMeSlide("https://www.youtube.com/embed/9TL9AIvJofU", "2:12"),
    toWalkMeSlide("https://www.youtube.com/embed/WDKnGVF6nyc", "1:04"),
    toWalkMeSlide("https://www.youtube.com/embed/Blv3CYhnz6c", "3:39"),
    toWalkMeSlide("https://www.youtube.com/embed/YgM2eHb7jMQ", "1:31"),
    toWalkMeSlide("https://www.youtube.com/embed/FREiW5cnoCM", "2:24"),
    toWalkMeSlide("https://www.youtube.com/embed/OHeN6xzqzA8", "1:20"),
    toWalkMeSlide("https://www.youtube.com/embed/djgCUclifR0", "1:31"),
    toWalkMeSlide("https://www.youtube.com/embed/Uz2mgJrX5ys", "1:27"),
  ];
  carouselSlides2: VngCarouselSlide[] = [
    toWalkMeSlide("https://www.youtube.com/embed/DtyZby0m2c8", "3:11"),
    toWalkMeSlide("https://www.youtube.com/embed/gIWlxiAVnbg", "3:11"),
    toWalkMeSlide("https://www.youtube.com/embed/WVYgnRXefLU", "1:13"),
    toWalkMeSlide("https://www.youtube.com/embed/m7I0fVtJzqw", "3:07"),
    toWalkMeSlide("https://www.youtube.com/embed/QzuL9vARewU", "1:38"),
    toWalkMeSlide("https://www.youtube.com/embed/GL07zxwJ5Zw", "1:01"),
    toWalkMeSlide("https://www.youtube.com/embed/y1gA5EMS20c", "1:20"),
    toWalkMeSlide("https://www.youtube.com/embed/gnZyQ6P1KA8", "1:55"),
    toWalkMeSlide("https://www.youtube.com/embed/iV0ub9bekIo", "1:10"),
    toWalkMeSlide("https://www.youtube.com/embed/vXS4S5ZtaL4", "0:57"),
    toWalkMeSlide("https://www.youtube.com/embed/wu88c101Mks", "1:06"),
    toWalkMeSlide("https://www.youtube.com/embed/l__13f5tLpY", "1:01"),
    toWalkMeSlide("https://www.youtube.com/embed/8EYYQlBs81c", "0:47"),
    toWalkMeSlide("https://www.youtube.com/embed/Uz2mgJrX5ys", "1:27"),
  ];

  determinate: any;
  step = -1;
  isInstalled = false;
  userPlanId: string;
  usedTrial: any;

  /**
   * Issue #518: while step 0 ("Install the Extension") is on screen and the
   * extension isn't detected yet, poll for a live install every 4s so the
   * step notices it and checks itself off without the user having to close
   * and reopen the modal. `isExtensionInstalled({ fresh: true })` is used
   * here — the service's session cache only short-circuits POSITIVE results,
   * so a plain call would never re-probe once cached, but this poll's whole
   * job is to catch the FIRST positive, at which point it stops itself.
   */
  private static readonly INSTALL_POLL_INTERVAL_MS = 4000;
  private installPollHandle: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly sharedService: SharedDataService,
    private readonly authService: AuthService,
    @Inject(VNG_DIALOG_DATA) public data: any,
    public dialogRef: VngDialogRef<WelcomeModalComponent>,
    public dialog: DialogService,
    private readonly router: Router,
    private readonly extensionService: ExtensionService,
    private readonly globalHookService: GlobalHookService,
    public sharedData: SharedDataService,
    private readonly dataService: DataService,
    private readonly stripeService: StripeService,
    private readonly onboardingProgress: OnboardingProgressService
  ) {
    if (data.step !== undefined) {
      this.step = Math.min(data.step, this.maxStep);

      this.extensionService.isExtensionInstalled().then((isInstalled) => {
        this.isInstalled = isInstalled;
        if (this.step === 0 && this.isInstalled) {
          this.step++;
          this.adjustModalWidth();
          lastValueFrom(this.onboardingProgress.completeStep("install_extension", this.step, this.maxStep + 1));
        }
      });
    }
  }

  get startTrial() {
    return false;
    // return this.userPlanId == "freemium" && this.usedTrial !== "true";
  }

  get user(): UserDetails {
    return this.authService.userDetails.user_details;
  }

  /** Centralized in OnboardingProgressService (issue #523) — this getter is now a thin passthrough. */
  get maxStep() {
    return this.onboardingProgress.maxStep;
  }

  get showFlyLearning() {
    return this.onboardingProgress.showFlyLearning;
  }

  /**
   * Issue #518: browser-aware label for the install step's download button
   * ("Add to Chrome" / "Add to Edge"), folded in from the retired
   * onboarding-0 wizard page (`download-extension-step.component.ts`). Only
   * Chrome and Edge are actually distributed, so anything else (Firefox,
   * Opera, Safari detections) reads as "Chrome" rather than an unsupported
   * browser name — same default the wizard page used.
   */
  get extensionButtonBrowserName(): string {
    return this.extensionService.browserName === "Edge" ? "Edge" : "Chrome";
  }

  /** Issue #518: which `step0-pin-{{type}}.png` asset to show — only chrome/edge variants exist. */
  get pinAssetBrowserType(): "chrome" | "edge" {
    return this.extensionService.browser_type === "edge" ? "edge" : "chrome";
  }

  ngOnInit(): void {
    this.userPlanId = this.sharedData.getAttribute("planId");
    this.usedTrial = this.sharedData.getAttribute("usedTrial");

    this.adjustModalWidth();
    this.extensionService.isExtensionInstalled().then((isInstalled) => {
      this.isInstalled = isInstalled;
      if (this.step === 0 && this.isInstalled) {
        this.step++;
        this.adjustModalWidth();
        lastValueFrom(this.onboardingProgress.completeStep("install_extension", this.step, this.maxStep + 1));
      }
    });

    // Issue #523: the "Resume/Retake Onboarding" buttons — and with them the
    // "we've updated onboarding, give it another go" popover that used to
    // anchor on the Resume button — are removed (the full-page wizard they
    // led to is being retired by #524). `onboardingv2_presented` is still
    // read elsewhere (dashboard.component.ts) as "has this user been shown
    // onboarding at all", so it's still set here — just silently, once, on
    // first render of this modal, instead of via that popover's Dismiss/Show
    // me actions.
    if (!this.user.onboardingv2_presented) {
      lastValueFrom(
        this.authService.patchOnboarding(this.user.id, this.user.onboardingv2_step, this.maxStep + 1, undefined, false, true)
      );
    }
  }

  startTrialNow() {
    this.stripeService.startSalesProTrial().subscribe({
      next: (response: any) => {
        return;
      },
      error: (error) => { },
    });
  }

  backToOnboarding() {
    this.setStep(Math.min(this.authService.userDetails.user_details.onboardingv2_step, this.maxStep));
  }
  //#region Video players
  // Issue #519: renumbered 2-8 (was 1-7) — `about_you` was inserted as panel
  // 1 (no video), shifting every video-having panel down by one. Issue #521:
  // panel 2 ("Add a FlyCut" -> the signature step) lost its video too, so the
  // video-having panels are now 3-8. The property name suffix must equal the
  // panel index passed to onPlayerReady()/onStateChange() below (they do
  // dynamic `this[`step${step}...`]` lookups), which in turn must equal that
  // panel's WELCOME_MODAL_STEP_ORDER index.
  @ViewChild("step3Video", { static: false }) step3Video!: YouTubePlayer;
  @ViewChild("step4Video", { static: false }) step4Video!: YouTubePlayer;
  @ViewChild("step5Video", { static: false }) step5Video!: YouTubePlayer;
  @ViewChild("step6Video", { static: false }) step6Video!: YouTubePlayer;
  @ViewChild("step7Video", { static: false }) step7Video!: YouTubePlayer;
  @ViewChild("step8Video", { static: false }) step8Video!: YouTubePlayer;
  step3VideoDuration: number = 0;
  step4VideoDuration: number = 0;
  step5VideoDuration: number = 0;
  step6VideoDuration: number = 0;
  step7VideoDuration: number = 0;
  step8VideoDuration: number = 0;
  step3WatchedPercentage: number = 0;
  step4WatchedPercentage: number = 0;
  step5WatchedPercentage: number = 0;
  step6WatchedPercentage: number = 0;
  step7WatchedPercentage: number = 0;
  step8WatchedPercentage: number = 0;
  step3Interval: any;
  step4Interval: any;
  step5Interval: any;
  step6Interval: any;
  step7Interval: any;
  step8Interval: any;

  onPlayerReady(event: any, step: number) {
    setTimeout(() => {
      this[`step${step}VideoDuration`] = this[`step${step}Video`].getDuration();
      this[`step${step}Video`].setVolume(20);
    }, 200);
  }

  onStateChange(event: any, step: number) {
    // Issue #523: gate on REAL per-step completion instead of the monotonic
    // `onboardingv2_step` counter — a step could be past its position without
    // the video actually being watched (e.g. skipped), and vice versa.
    const key = WELCOME_MODAL_STEP_ORDER[step];
    if (key && this.onboardingProgress.isCompleted(key)) {
      return;
    }

    if (event.data === 1 || event.data === 3) {
      // playing / buffering
      this.startTracking(step);
    } else if (event.data === 0) {
      // Issue #523: YouTube's "ended" state IS the 100%-watched signal. The
      // 2s poll below can miss the exact moment it crosses 100 (playback can
      // end between two ticks), so `ended` completes the row directly rather
      // than depending on the poll ever observing >=100.
      this[`step${step}WatchedPercentage`] = 100;
      this.completeVideoStep(step);
    } else {
      clearInterval(this[`step${step}Interval`]);
    }
  }

  startTracking(step: number) {
    const videoPlayer = this[`step${step}Video`];
    clearInterval(this[`step${step}Interval`]);

    // FIXED (#523): was `>= 75` — a step used to be marked complete at ~75%
    // watched (evidence reported the visible checkmark landing around 80% due
    // to poll timing). Completion now requires the video to be fully watched.
    if (this[`step${step}WatchedPercentage`] >= 100) {
      return;
    }

    this[`step${step}Interval`] = setInterval(() => {
      if (videoPlayer) {
        const currentTime = videoPlayer.getCurrentTime();
        this[`step${step}WatchedPercentage`] = Math.min(100, (currentTime / this[`step${step}VideoDuration`]) * 100);

        if (this[`step${step}WatchedPercentage`] >= 100) {
          this.completeVideoStep(step);
        }
      }
    }, 2000);
  }

  private completeVideoStep(step: number): void {
    clearInterval(this[`step${step}Interval`]);
    const key = WELCOME_MODAL_STEP_ORDER[step];
    if (!key || this.onboardingProgress.isCompleted(key)) {
      return;
    }
    lastValueFrom(this.onboardingProgress.completeStep(key, step + 1, this.maxStep + 1));
  }
  //#endregion Video players

  adjustModalWidth() {
    switch (this.step) {
      // case -1:
      //   if (this.dialogRef?.componentInstance?.dialogRef) {
      //     this.dialogRef.componentInstance.dialogRef.updateSize("495px");
      //   }
      //   break;
      case 0:
      case 1:
      case 2:
      case 3:
      case 4:
      case 5:
      case 6:
      case 7:
      case 8:
        if (this.dialogRef?.componentInstance?.dialogRef) {
          // Issue #522: was 800px. The checklist's left panel is now a fixed
          // 320px (welcome-modal.component.scss) to fit the renamed
          // "LinkedIn Social Post Generator" row without wrapping — widening
          // the whole dialog by the same amount keeps the right panel
          // (video/form) exactly as roomy as it was before, instead of
          // stealing that space from it.
          this.dialogRef.componentInstance.dialogRef.updateSize("900px");
        }
        break;
      default:
        if (this.dialogRef?.componentInstance?.dialogRef) {
          this.dialogRef.componentInstance.dialogRef.updateSize("400px");
        }
        break;
    }
    this.clearVideosInterval();
    this.manageInstallPoll();
  }

  /**
   * Issue #518: single hook for "the step changed" (every step-changing path
   * — constructor->ngOnInit, setStep(), next()/skip() via advance() — already
   * routes through `adjustModalWidth()`, exactly like `clearVideosInterval()`
   * above). Starts the live install poll only while step 0 is showing and
   * the extension isn't known-installed yet; stops it otherwise, including
   * on modal close (`ngOnDestroy` calls `stopInstallPoll()` directly since
   * `close()` doesn't itself call `adjustModalWidth()`).
   */
  private manageInstallPoll(): void {
    if (this.step === 0 && !this.isInstalled) {
      this.startInstallPoll();
    } else {
      this.stopInstallPoll();
    }
  }

  private startInstallPoll(): void {
    if (this.installPollHandle) {
      return;
    }
    this.installPollHandle = setInterval(() => {
      void this.pollForInstall();
    }, WelcomeModalComponent.INSTALL_POLL_INTERVAL_MS);
  }

  private stopInstallPoll(): void {
    if (this.installPollHandle) {
      clearInterval(this.installPollHandle);
      this.installPollHandle = null;
    }
  }

  /**
   * A fresh (cache-bypassing) re-probe. On detection: marks `install_extension`
   * complete — which reactively checks off the row and moves the header
   * percentage via `OnboardingProgressService.completed$` — and stops
   * polling. Mirrors `completeVideoStep()`'s pattern of completing a step
   * key WITHOUT forcing `this.step` to advance, so the user stays on the
   * panel (and its pin-to-browser visual) rather than being yanked forward
   * the instant the extension is detected.
   *
   * QA finding G-10 (issue #518): the standalone "Download the extension"
   * welcome nag can be open at the same time as this modal (see G-11,
   * pre-existing and out of scope). Before this fix, a live install detected
   * here ticked the step but left that nag on screen still demanding the
   * install for the rest of the session. closeDialogWithClass() dismisses it
   * the moment a live install is confirmed, regardless of whether this step
   * key happens to already be marked complete by some other path.
   */
  private async pollForInstall(): Promise<void> {
    const isInstalled = await this.extensionService.isExtensionInstalled({ fresh: true });
    if (!isInstalled) {
      return;
    }
    this.isInstalled = true;
    this.stopInstallPoll();
    this.dialog.closeDialogWithClass(WELCOME_NAG_DIALOG_CLASS);
    if (this.onboardingProgress.isCompleted("install_extension")) {
      return;
    }
    await lastValueFrom(this.onboardingProgress.completeStep("install_extension", 1, this.maxStep + 1));
  }

  ngOnDestroy(): void {
    this.stopInstallPoll();
  }

  setStep(step: number) {
    this.step = Math.min(step, this.maxStep);
    this.adjustModalWidth();
  }

  clearVideosInterval() {
    setTimeout(() => {
      clearInterval(this.step3Interval);
      clearInterval(this.step4Interval);
      clearInterval(this.step5Interval);
      clearInterval(this.step6Interval);
      clearInterval(this.step7Interval);
      clearInterval(this.step8Interval);
    }, 1000);
  }

  /**
   * Advances the modal to the next panel, persisting through the given
   * observable, then closes + surfaces the learning-resources tooltip if that
   * advance moved past the last tracked step. Shared tail behavior for both
   * `next()` (real completions) and `skip()` (navigation only).
   */
  private async advance(step: number, persist$: Observable<unknown>): Promise<void> {
    await lastValueFrom(persist$);
    this.step = Math.min(step, this.maxStep);
    this.adjustModalWidth();
    if (step > this.maxStep && this.globalHookService.components?.topBar) {
      this.close();
      this.globalHookService.components.topBar.showLearningResourcesTooltip();
    }
  }

  /**
   * A REAL completion (the "Try it out!"/"Get started" CTAs, and the
   * extension-installed auto-advance). Marks the panel being LEFT as complete
   * via OnboardingProgressService — never called by skip buttons (see `skip()`).
   */
  async next() {
    this.clearVideosInterval();
    const originalStep = this.step;
    let step = originalStep + 1;
    let completedKey: OnboardingStepKey | undefined = WELCOME_MODAL_STEP_ORDER[originalStep];

    if (step === 0) {
      const isInstalled = await this.extensionService.isExtensionInstalled();
      this.isInstalled = isInstalled;
      if (this.isInstalled) {
        step++;
        completedKey = "install_extension";
      }
    }

    const persist$ = completedKey
      ? this.onboardingProgress.completeStep(completedKey, step, this.maxStep + 1)
      : this.onboardingProgress.goToStep(step, this.maxStep + 1);

    await this.advance(step, persist$);
  }

  /**
   * FIXED (#523): "I'll try it later"/"Skip, ..." buttons used to call
   * `next()`, which persisted the same way a real completion did — making a
   * skipped row look done. Skip now ONLY advances the legacy navigation
   * position (so the next panel is reachable) and never touches the
   * completed-step set.
   */
  skip(): void {
    this.clearVideosInterval();
    const step = this.step + 1;
    void this.advance(step, this.onboardingProgress.goToStep(step, this.maxStep + 1));
  }

  goToFlyLearning() {
    let user = this.authService.userDetails;
    let email = user.user_details?.email;

    const data = { Email: email };
    const url = "v1/authenticateUser";
    this.dataService.post({ url, data, isLoader: true }).subscribe(async (response: any) => {
      if (response?.result) {
        let redirect_url = response.result.redirect_url;
        if (redirect_url) {
          window.open(redirect_url, "_blank").focus();
          this.next();
        }
      }
    });
  }

  navigateToMoreLearningVideos() {
    this.step = 9;
  }

  /**
   * Issue #521: `app-add-flycut-signature-step` (panel 2) already opened the
   * real AddShortcutComponent, fully pre-filled, before emitting this — no
   * `next()` here (unlike every other panel's "Try it out!"): the checklist
   * row completes on an actual FlyCut save, not on this click (see
   * `add-shortcut.component.ts`'s `createShortcut()`). This only hides the
   * walk-me modal so the pre-filled dialog is the one thing on screen.
   */
  onSignatureTryItOut() {
    this.close();
  }

  navigateToExtension() {
    this.extensionService.openExtensionStorePage();
  }

  tryItOnLinkedIn() {
    this.next();
    window.open("https://www.linkedin.com", "_blank");
  }

  tryAddAFlyPlate() {
    this.next();
    this.close();
    if (this.globalHookService.components.menu) {
      this.globalHookService.components.menu?.startWalkMeStepTree();
    }
  }

  tryPersona() {
    this.next();
    this.close();
    this.router.navigate([`/settings`], { queryParams: { tab: "Persona" } });
  }

  playVideo1(event: VngCarouselSlideClickEvent) {
    this.openVideoDialog(event);
  }
  playVideo2(event: VngCarouselSlideClickEvent) {
    this.openVideoDialog(event);
  }

  /**
   * The video player fills the whole panel, so the dialog must be edge-to-edge.
   * The panel's padding is an inline style written by VngDialogService, which
   * the `.video-dialog` panel-class rule in app.scss cannot beat — hence the
   * explicit `padding: "0"` passthrough.
   */
  private openVideoDialog(event: VngCarouselSlideClickEvent) {
    this.dialog.openDialogComponent(
      VideoComponent,
      { self: this, class: "video-dialog", link: event.slide.data },
      "800px",
      false,
      undefined,
      { padding: "0" }
    );
  }

  tryTestAFlyCut() {
    this.next();
    this.close();
    if (this.globalHookService.components.dashboard) {
      this.globalHookService.components.dashboard?.startWalkMeStepTwo();
    }
  }

  tryPersonasLater() {
    this.skip();
  }

  tryFlyCutLater() {
    this.skip();
  }

  tryFlyEngageLater() {
    this.skip();
  }

  tryFlyPostLater() {
    this.skip();
  }

  @ViewChild("nav1") slider1: VngCarouselComponent;
  @ViewChild("nav2") slider2: VngCarouselComponent;

  prevImageClick() {
    this.slider1.prev();
    this.slider2.prev();
  }

  nextImageClick() {
    this.slider1.next();
    this.slider2.next();
  }

  close(end: boolean = false) {
    // Issue #518: stop polling as soon as the modal is asked to close, not
    // only on ngOnDestroy — VngDialogService may not tear the component down
    // synchronously, and a poll left running after close would keep patching
    // onboarding state for a modal the user can no longer see.
    this.stopInstallPoll();
    if (end) {
      this.globalHookService.onboardingActive = false;

      if (this.globalHookService.components?.topBar) {
        this.close();
        this.globalHookService.components.topBar.showLearningResourcesTooltip();
        return;
      }
    }

    this.dialogRef.close(this.step);
  }
}
